import { type FC, forwardRef } from 'react';

import useChart, { parseChartDecimals } from 'hooks/useChart/useChart';
import HChart from '@components/HChart/HChart';
import { useTheme } from 'context/ThemeProvider/ThemeProvider';
import type Highcharts from 'highcharts/highstock';
import { type ChangeTS } from 'interfaces/change/ChangeTS.interface';
import { parseUnitType } from 'utils/formatters/units/getReadableUnitByName';
import { convertToReadableDate } from 'utils/dateUtils/dateUtils';

interface BuildingMeterChartProps {
  unit?: string;
  dataGrouping?: [string, number[] | null][] | undefined;
  data?: ChangeTS[] | null;
  xAxisEvents?: Highcharts.XAxisEventsOptions | undefined;
  changesData?: any;
  notes?: any[];
  onNoteClick?: () => void;
  isNonConsumption: boolean;
}

interface SavingsOption {
  x: number;
  y: number;
  impactPerDay: number;
  expectedConsumption: number;
  actualConsumption: number;
  time: string;
}

const BuildingMeterChart: FC<BuildingMeterChartProps> = forwardRef(
  (
    {
      unit = '',
      dataGrouping,
      data,
      xAxisEvents,
      changesData,
      notes,
      onNoteClick,
      isNonConsumption = false,
    },
    _ref
  ) => {
    const { theme } = useTheme();
    const { defaultOptions, changes, labelFormatter } = useChart({
      chartData: data,
      unit: parseUnitType(unit),
      type: 'measured',
      changesData,
    });

    function handleNoteClick(this: Highcharts.Point): void {
      if (onNoteClick) {
        onNoteClick();
      }
    }

    function pointFormatter(this: Highcharts.Point): string {
      const options = this.options as SavingsOption;
      const [time] = options.time.split('GMT');

      const timeString = `Time: <b>${convertToReadableDate(time)}</b><br/>`;
      const actualConsumption = `Actual consumption: <b>${
        parseChartDecimals(this.y) ?? 0
      } ${parseUnitType(unit)}</b></br>`;

      return `${timeString}${actualConsumption}`;
    }

    const options: Highcharts.Options = {
      ...defaultOptions,
      chart: {
        ...defaultOptions.chart,
        events: {
          ...defaultOptions.chart?.events,
        },
      },
      tooltip: {
        useHTML: true,
        style: {
          width: 300,
          textAlign: 'justify',
        },
        ...defaultOptions.tooltip,
      },
      xAxis: {
        plotLines: changes,
        events: {
          ...xAxisEvents,
        },
        range: 365 * 24 * 3600 * 1000,
      },

      yAxis: [
        {
          labels: {
            formatter: labelFormatter,
          },
          title: {
            text: 'Energy Use',
          },
        },
      ],
      series: [
        {
          name: 'Measured',
          type: 'column',
          color: theme.colors.tertiary,
          borderColor: theme.colors.tertiary,
          borderWidth: 1,
          data: data?.map((ts, index) => ({
            x: new Date(ts.time).getTime(),
            y: ts.energy_use,
            impactPerDay: ts.savings,
            actualConsumption: ts.energy_use,
            expectedConsumption: ts.result_baseline,
            time: ts.time,
          })),
          dataGrouping: {
            forced: true,
            approximation: isNonConsumption ? 'average' : 'sum',
            groupPixelWidth: 10,
            units: dataGrouping ?? [
              ['hour', [1]],
              ['day', [1]],
              ['week', [1]],
              ['month', [1, 3, 6]],
              ['year', null],
            ],
          },
          tooltip: {
            pointFormatter,
          },
          turboThreshold: data?.length,
          yAxis: 0,
        },
        {
          animation: {
            duration: 0,
          },
          color: theme.colors.primary,
          borderColor: theme.colors.alert,
          borderWidth: 1,
          type: 'line',
          name: 'Expected consumption',
          data: data?.map((ts, index) => [
            new Date(ts.time).getTime(),
            ts.result_baseline,
          ]),
          dataGrouping: {
            forced: true,
            approximation: 'sum',
            groupPixelWidth: 10,
            units: dataGrouping ?? [
              ['hour', [1]],
              ['day', [1]],
              ['week', [1]],
              ['month', [1, 3, 6]],
              ['year', null],
            ],
          },
          yAxis: 0,
          turboThreshold: data?.length,
          enableMouseTracking: false,
        },
        // Notes scatter series - only show when data is loaded
        ...(data && data.length > 0 && notes && notes.length > 0
          ? [
              {
                name: 'Notes',
                custom: { isNote: true },
                type: 'scatter' as const,
                data: notes
                  .filter((note) => {
                    // Filter out notes that don't have corresponding chart data
                    if (!data || data.length === 0) return false;

                    let noteDate = note.date;
                    if (
                      typeof noteDate === 'string' &&
                      !noteDate.includes('Z') &&
                      !noteDate.includes('+') &&
                      !noteDate.includes('-', 10)
                    ) {
                      noteDate = noteDate + 'Z';
                    }

                    const noteTimestamp = new Date(noteDate).getTime();

                    // Get the date range of available chart data
                    const chartDates = data.map((ts) =>
                      new Date(ts.time).getTime()
                    );
                    const minChartDate = Math.min(...chartDates);
                    const maxChartDate = Math.max(...chartDates);

                    return (
                      noteTimestamp >= minChartDate &&
                      noteTimestamp <= maxChartDate
                    );
                  })
                  .map((note) => {
                    const given = String(
                      note?.created_by_user?.given_name ?? ''
                    );
                    const family = String(
                      note?.created_by_user?.family_name ?? ''
                    );
                    const createdBy = `${given} ${family}`.trim();

                    // Handle timezone properly - if no timezone info, treat as UTC
                    let noteDate = note.date;
                    if (
                      typeof noteDate === 'string' &&
                      !noteDate.includes('Z') &&
                      !noteDate.includes('+') &&
                      !noteDate.includes('-', 10)
                    ) {
                      noteDate = noteDate + 'Z';
                    }

                    return {
                      x: new Date(noteDate).getTime(),
                      y: 0,
                      description: note.description,
                      date: note.date,
                      createdBy,
                      createdAt: note.created_at,
                      letterIndex: note.letter_index,
                    };
                  }),
                marker: {
                  lineWidth: 1,
                  lineColor: 'black',
                  symbol: 'circle' as const,
                  radius: 8,
                  fillColor: '#fff',
                  states: {
                    hover: {
                      radiusPlus: 2,
                    },
                  },
                },
                tooltip: {
                  headerFormat: '',
                  pointFormatter(this: any) {
                    const description: string = this.options?.description || '';
                    const date: string = this.options?.createdAt || '';
                    const createdBy: string =
                      this.options?.createdBy || 'Unknown User';
                    return `
                      <div style="
                        max-width: 280px;
                        width: 280px;
                        word-wrap: break-word;
                        white-space: normal;
                      ">
                        <strong style="color: #eee; font-size: 12px;">NOTE</strong><br/><br/>
                        <div style="
                          color: #eee;
                          font-size: 12px;
                          line-height: 1.4;
                          word-wrap: break-word;
                          white-space: normal;
                          overflow-wrap: break-word;
                          hyphens: auto;
                        ">${description}</div><br/>
                        <div style="
                          display: flex;
                          justify-content: flex-start;
                          flex-wrap: wrap;
                          gap: 8px;
                        ">
                          <span style="color: rgba(238, 238, 238, 0.5); font-size: 12px; font-weight: 500;">${convertToReadableDate(
                            date
                          )}</span>
                          <span style="color: rgba(238, 238, 238, 0.5); font-size: 12px; font-weight: 500;">${createdBy}</span>
                        </div>
                      </div>
                    `;
                  },
                },
                yAxis: 0,
                showInLegend: true,
                enableMouseTracking: true,
                stickyTracking: false,
                findNearestPointBy: 'xy' as const,

                dataLabels: {
                  enabled: true,
                  allowOverlap: true,
                  formatter: function (this: any) {
                    // Use letter_index from backend if available, otherwise fall back to user initial
                    const letterIndex = this.point?.options?.letterIndex;
                    if (letterIndex) {
                      const displayLetter = String(letterIndex).toUpperCase();
                      return displayLetter;
                    } else {
                      // Fallback to user initial if letter_index is not available
                      const createdBy = this.point?.options?.createdBy || '';
                      const givenName = createdBy.split(' ')[0] || '';
                      const initial = String(givenName.charAt(0).toUpperCase());
                      return initial;
                    }
                  },
                  useHTML: false,
                  crop: false,
                  overflow: 'allow' as const,
                  x: 0,
                  y: -1,
                  style: {
                    fontSize: '11px',
                    fontWeight: 'normal',
                    color: 'black',
                    textAlign: 'center',
                    textOutline: 'none',
                  },
                  align: 'center' as const,
                  verticalAlign: 'middle' as const,
                },
                point: {
                  events: {
                    click: handleNoteClick,
                  },
                },
              },
            ]
          : []),
      ],
    };

    return <>{options && <HChart options={options} />}</>;
  }
);
export default BuildingMeterChart;
